/**
 * Seolari sections: small, independent Web Components ported from the SEOLARI PDP design.
 * Each component scopes itself to its own section instance.
 */

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

/** Calls fn at most once per frame on scroll/resize. Capture phase: on desktop Horizon scrolls .page-wrapper, not the window. */
const onScrollFrame = (fn) => {
  let raf = 0;
  const handler = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      fn();
    });
  };
  document.addEventListener('scroll', handler, { passive: true, capture: true });
  window.addEventListener('resize', handler, { passive: true });
  return () => {
    document.removeEventListener('scroll', handler, { capture: true });
    window.removeEventListener('resize', handler);
  };
};

/** Scrolls an element by `delta` instantly, without snapping or smooth scrolling getting in the way. */
const jump = (track, delta) => {
  track.style.scrollBehavior = 'auto';
  track.style.scrollSnapType = 'none';
  track.scrollLeft += delta;
  track.style.scrollSnapType = '';
  track.style.scrollBehavior = '';
};

/** Adds a decorative copy of every child before and after the originals, for an infinite carousel. */
const cloneAround = (track, extraClass = '') => {
  const items = [...track.children];
  const copy = (item) => {
    const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.dataset.clone = '';
    if (extraClass) clone.classList.add(extraClass);
    // Keep theme-editor block selection on the original, not its copies.
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('a, button, video, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
      el.removeAttribute('aria-label');
      if (el.tagName === 'VIDEO') el.preload = 'none';
    });
    clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));
    return clone;
  };
  track.prepend(...items.map(copy));
  track.append(...items.map(copy));
  return items.length;
};

/** Plays a muted, looping video only while it is on screen. */
class SeLazyVideo extends HTMLElement {
  static observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (entry.isIntersecting) {
          video.preload = 'auto';
          video.play()?.catch(() => {});
        } else {
          video.pause();
        }
      }
    },
    { rootMargin: '100px 0px', threshold: 0.15 }
  );

  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    Object.assign(this.video, { muted: true, defaultMuted: true, loop: true, playsInline: true, controls: false });
    this.video.removeAttribute('controls');
    // Videos inside an infinite carousel are played/paused by the carousel (se-loop) as a group.
    if (this.closest('se-loop')) return;
    SeLazyVideo.observer.observe(this.video);
  }

  disconnectedCallback() {
    if (this.video) SeLazyVideo.observer.unobserve(this.video);
  }
}

/** Product gallery: grid on desktop, infinite swipe carousel with thumbnails on mobile. */
class SeGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.se-g-track');
    this.thumbs = [...this.querySelectorAll('.se-thumb')];
    if (!this.track) return;
    this.count = cloneAround(this.track, 'se-gclone');
    this.index = 0;

    this.addEventListener('click', (event) => {
      const thumb = /** @type {HTMLElement} */ (event.target).closest('.se-thumb');
      if (thumb) this.go(Number(thumb.dataset.slide));
    });
    this.track.addEventListener('scroll', () => this.sync(), { passive: true });
    requestAnimationFrame(() => this.sync());
  }

  /** True while the track is the mobile carousel (clones visible). */
  get looping() {
    const t = this.track;
    return t.clientWidth > 0 && t.scrollWidth > t.clientWidth * (this.count + 1);
  }

  sync() {
    if (!this.looping) return;
    const t = this.track;
    const w = t.clientWidth;
    const n = this.count;
    if (!t.dataset.ready) {
      t.dataset.ready = '1';
      jump(t, n * w - t.scrollLeft);
    }
    if (t.scrollLeft < (n / 2) * w) jump(t, n * w);
    else if (t.scrollLeft > (2 * n + n / 2 - 1) * w) jump(t, -n * w);
    const r = Math.round(t.scrollLeft / w);
    this.setActive((((r - n) % n) + n) % n);
  }

  go(index) {
    if (!this.looping) return;
    const w = this.track.clientWidth;
    this.track.scrollTo({ left: (this.count + index) * w, behavior: 'smooth' });
    this.setActive(index);
  }

  setActive(index) {
    if (index === this.index) return;
    this.index = index;
    this.thumbs.forEach((thumb, k) => {
      thumb.classList.toggle('se-on', k === index);
      thumb.setAttribute('aria-current', String(k === index));
    });
  }
}

/** Infinite horizontal carousel (UGC strip, before/after, stories) with optional prev/next and progress bar. */
class SeLoop extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-loop-track]');
    this.fill = this.querySelector('[data-loop-fill]');
    if (!this.track || !this.track.children.length) return;
    this.count = cloneAround(this.track);

    this.addEventListener('click', (event) => {
      const btn = /** @type {HTMLElement} */ (event.target).closest('[data-dir]');
      if (btn) this.step(Number(btn.dataset.dir));
    });
    this.track.addEventListener('scroll', () => this.fix(), { passive: true });
    requestAnimationFrame(() => this.fix());

    this.videos = [...this.track.children].map((kid) => kid.querySelector('video'));
    if (this.videos.some(Boolean)) {
      this.io = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.play() : this.pause()),
        { rootMargin: '100px 0px' }
      );
      this.io.observe(this);
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  /**
   * Every copy plays while the carousel is on screen, so a loop jump never lands on a paused video.
   * The first time, all copies of a clip start together from frame 0 so they stay in step without seeking.
   */
  play() {
    if (!this.started) {
      this.started = true;
      this.videos.forEach((video) => {
        if (!video) return;
        video.preload = 'auto';
        try {
          video.currentTime = 0;
        } catch {}
      });
    }
    this.videos.forEach((video) => video?.play()?.catch(() => {}));
  }

  pause() {
    this.videos.forEach((video) => video?.pause());
  }

  /**
   * Before jumping one set left/right, correct copies we land on only if they drifted noticeably;
   * every seek makes a video stall briefly, so small differences are left alone.
   * @param {number} dir 1 when jumping forward (from the first copy to the originals), -1 when jumping back
   */
  syncVideos(dir) {
    if (!this.videos?.some(Boolean)) return;
    const n = this.count;
    for (let i = 0; i < n; i++) {
      const from = this.videos[dir > 0 ? i : i + 2 * n];
      const to = this.videos[i + n];
      if (!from || !to || from.readyState < 2 || to.readyState < 2) continue;
      if (Math.abs(to.currentTime - from.currentTime) > 0.3) {
        try {
          to.currentTime = from.currentTime;
        } catch {}
      }
    }
  }

  /** Keeps the scroll position inside the middle copy and updates the progress bar. */
  fix() {
    const t = this.track;
    const kids = t.children;
    const set = kids[this.count].offsetLeft - kids[0].offsetLeft;
    if (!set) return;
    if (!t.dataset.ready) {
      t.dataset.ready = '1';
      jump(t, set - t.scrollLeft);
    }
    if (t.scrollLeft < set * 0.5) {
      this.syncVideos(1);
      jump(t, set);
    } else if (t.scrollLeft > set * 1.5) {
      this.syncVideos(-1);
      jump(t, -set);
    }
    if (this.fill) {
      // Progress = position of the first visible card within one set (card 1 of 4 → 25%, 4 of 4 → 100%).
      const pos = (((t.scrollLeft - set) % set) + set) % set;
      const step = set / this.count;
      const current = Math.round(pos / step) % this.count;
      this.fill.style.width = `${((current + 1) / this.count) * 100}%`;
    }
  }

  step(dir) {
    this.fix();
    const card = /** @type {HTMLElement} */ (this.track.firstElementChild);
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
    const w = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: dir * w, behavior: 'smooth' });
  }
}

/** One-open-at-a-time accordion. Items: [data-acc-item] > [data-acc-btn] + optional [data-acc-panel]. */
class SeAccordion extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const btn = /** @type {HTMLElement} */ (event.target).closest('[data-acc-btn]');
      if (!btn || !this.contains(btn)) return;
      const item = btn.closest('[data-acc-item]');
      const open = !item.classList.contains('se-open');
      this.querySelectorAll('[data-acc-item]').forEach((el) => this.set(el, el === item && open));
    });
  }

  set(item, open) {
    const wasOpen = item.classList.contains('se-open');
    item.classList.toggle('se-open', open);
    item.querySelector('[data-acc-btn]')?.setAttribute('aria-expanded', String(open));
    const panel = /** @type {HTMLElement | null} */ (item.querySelector('[data-acc-panel]'));
    if (!panel || wasOpen === open) return;
    this.animate(panel, open);
  }

  /** Slides the panel open/closed by animating its height. */
  animate(panel, open) {
    panel.anim?.cancel();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.hidden = !open;
      return;
    }
    const from = panel.hidden ? 0 : panel.getBoundingClientRect().height;
    panel.hidden = false;
    const to = open ? panel.scrollHeight : 0;
    panel.style.overflow = 'hidden';
    panel.anim = panel.animate(
      [{ height: `${from}px`, opacity: open ? 0 : 1 }, { height: `${to}px`, opacity: open ? 1 : 0 }],
      { duration: 320, easing: 'cubic-bezier(.33,1,.68,1)' }
    );
    panel.anim.onfinish = () => {
      panel.anim = null;
      panel.hidden = !open;
      panel.style.overflow = '';
    };
  }
}

/** Buy fold: offer picker, delivery date, "How does it work?" sheet, sticky bar and CheckoutChamp checkout. */
class SeProduct extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('.se-opt')];
    this.atc = this.querySelector('[data-atc]');
    this.sheet = this.querySelector('.se-ov');
    this.sticky = this.querySelector('.se-sk');

    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const card = target.closest('.se-opt');
      if (card) return this.select(card);
      if (target.closest('[data-checkout]')) return this.checkout();
      if (target.closest('[data-how-open]')) return this.openHow();
      if (target.closest('[data-how-close]') || target === this.sheet) this.closeHow();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.sheet && !this.sheet.hidden) this.closeHow();
    });

    this.querySelectorAll('[data-days-ahead]').forEach((el) => {
      const d = new Date();
      d.setDate(d.getDate() + (Number(/** @type {HTMLElement} */ (el).dataset.daysAhead) || 0));
      el.textContent = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    });

    if (this.sticky && this.atc) {
      this.offScroll = onScrollFrame(() => {
        this.sticky.classList.toggle('se-on', this.atc.getBoundingClientRect().bottom < 0);
      });
    }
  }

  disconnectedCallback() {
    this.offScroll?.();
  }

  get selected() {
    return this.cards.find((card) => card.classList.contains('se-on')) || this.cards[0];
  }

  select(card) {
    this.cards.forEach((c) => {
      const on = c === card;
      c.classList.toggle('se-on', on);
      c.setAttribute('aria-checked', String(on));
    });
    const price = this.querySelector('[data-sticky-price]');
    if (price && card.dataset.price) price.textContent = card.dataset.price;
  }

  checkout() {
    if (this.dataset.checkoutMode === 'shopify') return this.shopifyCheckout();
    const domain = this.dataset.cocDomain || '';
    const cocId = this.selected?.dataset.cocId || '';
    let url = domain + '?products=' + cocId + ':1';
    const utms = window.location.search.replace(/^\?/, '');
    if (utms) url += '&' + utms;
    window.location.href = url;
  }

  /**
   * Native Shopify checkout, one-time purchase: a cart permalink (/cart/<variant>:1) replaces the cart with just
   * the selected variant (no selling plan) and redirects to checkout server-side. No AJAX cart calls, so storefront
   * scripts that hook /cart/add.js or checkout buttons (e.g. CheckoutChamp) can't divert it.
   */
  shopifyCheckout() {
    const variantId = this.selected?.dataset.variantId;
    if (!variantId) return;
    const root = window.Shopify?.routes?.root || '/';
    window.location.assign(`${root}cart/${variantId}:1${window.location.search}`);
  }

  openHow() {
    if (!this.sheet) return;
    this.sheet.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.sheet.querySelector('video')?.play()?.catch(() => {});
    /** @type {HTMLElement} */ (this.sheet.querySelector('[data-how-close]'))?.focus();
  }

  closeHow() {
    if (!this.sheet) return;
    this.sheet.hidden = true;
    this.sheet.querySelector('video')?.pause();
    document.documentElement.style.removeProperty('overflow');
  }
}

/** Mechanism stepper: the step whose dot has passed 60% of the viewport is lit; the rail runs dot to dot. */
class SeMechanism extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.se-mx-item')];
    this.dots = this.items.map((item) => item.querySelector('.se-mx-dot'));
    this.rail = this.querySelector('.se-mx-rail');
    this.fill = this.querySelector('.se-mx-fill');
    this.current = -1;
    if (!this.items.length) return;
    this.offScroll = onScrollFrame(() => this.update());
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(this);
  }

  disconnectedCallback() {
    this.offScroll?.();
    this.ro?.disconnect();
  }

  /** Dot centres relative to this element; only change on resize, not on scroll. */
  layout() {
    const box = this.getBoundingClientRect();
    this.mids = this.dots.map((d) => {
      const r = d.getBoundingClientRect();
      return r.top + r.height / 2 - box.top;
    });
    const top = this.mids[0];
    const bottom = this.mids[this.mids.length - 1];
    if (this.rail) {
      this.rail.style.top = `${top}px`;
      this.rail.style.height = `${Math.max(0, bottom - top)}px`;
    }
    this.current = -1;
    this.update();
  }

  /** Every step whose dot has passed 60% of the viewport is filled; the last one is the current step. */
  update() {
    if (!this.mids) return;
    const box = this.getBoundingClientRect();
    const line = window.innerHeight * 0.6 - box.top;
    let step = 0;
    this.mids.forEach((m, i) => { if (m <= line) step = i; });
    if (step === this.current) return;
    this.current = step;
    this.items.forEach((item, i) => {
      item.classList.toggle('se-done', i <= step);
      item.classList.toggle('se-on', i === step);
    });
    if (this.fill) {
      const top = this.mids[0];
      const span = this.mids[this.mids.length - 1] - top || 1;
      this.fill.style.height = `${((this.mids[step] - top) / span) * 100}%`;
    }
  }
}

/** Reviews: desktop "Load more" reveals the next group of 4; mobile "See more reviews" swaps the row for the full list. */
class SeReviews extends HTMLElement {
  connectedCallback() {
    this.groups = [...this.querySelectorAll('.se-rv-grp')];
    this.more = this.querySelector('.se-rv-more');
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('[data-rv-load]')) this.load();
      else if (target.closest('[data-rv-all]')) this.showAll();
    });
  }

  load() {
    this.groups.find((g) => g.hidden)?.removeAttribute('hidden');
    if (this.more) this.more.hidden = !this.groups.some((g) => g.hidden);
  }

  showAll() {
    this.groups.forEach((g) => g.removeAttribute('hidden'));
    if (this.more) this.more.hidden = true;
    this.querySelector('.se-rv-mrow')?.classList.add('se-off');
    this.querySelector('.se-rv-g')?.classList.add('se-all');
  }
}

/**
 * Stat rings ([data-count-item]): each card fades up, its ring fills and its number counts up
 * once it is on screen. Cards that arrive together are queued so they play one by one.
 */
class SeCount extends HTMLElement {
  static stagger = 300;

  connectedCallback() {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.items = [...this.querySelectorAll('[data-count-item]')];
    if (!this.items.length) return;
    this.items.forEach((item) => {
      const fill = item.querySelector('[data-dash]');
      if (fill) fill.style.strokeDasharray = `0 ${fill.dataset.circ}`;
      const num = item.querySelector('[data-count]');
      if (num) {
        num.dataset.final = num.textContent;
        num.textContent = num.textContent.replace(/\d+(\.\d+)?/, '0');
      }
    });
    this.classList.add('se-anim');
    this.nextStart = 0;
    this.io = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .sort((x, y) => this.items.indexOf(x.target) - this.items.indexOf(y.target))
          .forEach((e) => {
            this.io.unobserve(e.target);
            this.queue(e.target);
          });
      },
      { threshold: 0.6, rootMargin: '0px 0px -10% 0px' }
    );
    this.items.forEach((item) => this.io.observe(item));
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  queue(item) {
    const now = performance.now();
    const start = Math.max(now + 150, this.nextStart);
    this.nextStart = start + SeCount.stagger;
    setTimeout(() => this.play(item), start - now);
  }

  play(item) {
    const duration = 1400;
    item.classList.add('se-in');
    const fill = item.querySelector('[data-dash]');
    if (fill) fill.style.strokeDasharray = `${fill.dataset.dash} ${fill.dataset.circ}`;
    const num = item.querySelector('[data-count]');
    const match = num?.dataset.final?.match(/\d+(\.\d+)?/);
    if (!num || !match) return;
    const target = parseFloat(match[0]);
    const decimals = match[1] ? match[1].length - 1 : 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      num.textContent = num.dataset.final.replace(match[0], (target * eased).toFixed(decimals));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

/** Header: mobile menu dialog; the cart icon checks out the bundle selected in the buy fold. */
class SeHeader extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('dialog');
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('[data-se-cart]')) {
        const product = /** @type {SeProduct | null} */ (document.querySelector('se-product'));
        if (product && typeof product.checkout === 'function') {
          event.preventDefault();
          product.checkout();
        }
        return;
      }
      if (target.closest('[data-menu-open]')) this.menu?.showModal();
      else if (target.closest('[data-menu-close]') || target === this.menu) this.menu?.close();
    });
  }
}

define('se-lazy-video', SeLazyVideo);
define('se-gallery', SeGallery);
define('se-loop', SeLoop);
define('se-accordion', SeAccordion);
define('se-product', SeProduct);
define('se-mechanism', SeMechanism);
define('se-reviews', SeReviews);
define('se-header', SeHeader);
define('se-count', SeCount);
